import { inject, InjectionToken, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SupabaseClient, createClient } from '@supabase/supabase-js';
import { environment } from '../../environments/environment';

/**
 * Supabase 客戶端的注入令牌
 * 用於在整個應用程序中注入統一的 Supabase 實例
 */
export const SUPABASE_CLIENT = new InjectionToken<SupabaseClient>('supabase.client');

/**
 * Supabase 提供者工廠函數
 * 負責創建和配置 Supabase 客戶端實例
 * 
 * @returns 配置好的 Supabase 客戶端實例
 */
export function supabaseFactory(): SupabaseClient {
  const platformId = inject(PLATFORM_ID);
  const authStorageKey = getAuthStorageKey();
  
  // 檢查是否在瀏覽器環境中運行
  if (!isPlatformBrowser(platformId)) {
    // 在服務器端返回一個基本的客戶端
    return createClient(environment.supabaseUrl, environment.supabaseKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false
      }
    });
  }

  // 在瀏覽器端創建完整功能的客戶端
  return createClient(environment.supabaseUrl, environment.supabaseKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: authStorageKey,
      storage: window.localStorage,
      debug: false,
      lock: waitForNavigatorLock
    },
    global: {
      headers: {
        'X-Client-Info': 'time-bank-web-app'
      }
    },
    // 添加 realtime 配置
    realtime: {
      params: {
        eventsPerSecond: 2
      }
    }
  });
}

/**
 * 遵守 Supabase 傳入的 acquireTimeout：
 * - 負數：等到取得鎖為止（登入、換 token 等主要操作）
 * - 0：拿不到就立即放棄（背景自動換 token 的檢查，下次 tick 再試）
 * - 正數：最多等待指定毫秒
 * 放棄時拋出帶 isAcquireTimeout 的錯誤，auth-js 會安靜略過，不會輸出鎖定警告。
 */
async function waitForNavigatorLock<R>(
  name: string,
  acquireTimeout: number,
  operation: () => Promise<R>
): Promise<R> {
  if (!globalThis.navigator?.locks) {
    return operation();
  }

  if (acquireTimeout === 0) {
    // 不在 LockManager 回呼內拋錯：原生 API 接手回呼 Promise 前，zone.js 會先把它當成未處理錯誤印出
    const result = await globalThis.navigator.locks.request(name, { mode: 'exclusive', ifAvailable: true }, async (lock) =>
      lock ? { acquired: true as const, value: await operation() } : { acquired: false as const }
    );
    if (!result.acquired) {
      throw createLockAcquireTimeoutError(name);
    }
    return result.value;
  }

  if (acquireTimeout > 0) {
    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), acquireTimeout);
    try {
      return await globalThis.navigator.locks.request(name, { mode: 'exclusive', signal: abortController.signal }, async () => {
        clearTimeout(timer);
        return operation();
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        throw createLockAcquireTimeoutError(name);
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  return globalThis.navigator.locks.request(name, { mode: 'exclusive' }, operation);
}

function createLockAcquireTimeoutError(name: string): Error & { isAcquireTimeout: true } {
  return Object.assign(new Error(`Acquiring lock "${name}" timed out`), { isAcquireTimeout: true as const });
}

function getAuthStorageKey(): string {
  try {
    const projectRef = new URL(environment.supabaseUrl).hostname.split('.')[0];
    return `sb-${projectRef}-auth-token`;
  } catch {
    return 'sb-time-bank-auth-token';
  }
}

/**
 * Supabase 提供者配置
 * 用於在 app.config.ts 中註冊
 */
export const SUPABASE_PROVIDER = {
  provide: SUPABASE_CLIENT,
  useFactory: supabaseFactory
};
